import { useRef, useState, useEffect, useCallback } from 'react';
import { API_URL } from '../config/apiUrl';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { setCurrentRecording } from '../redux/actions/currentRecording';
import { openSnackbar } from '../redux/actions/snackbarAction';
import { coerceDurationToSeconds } from '../utils/time';
import * as chunkQueue from '../utils/chunkQueue';

const INITIAL_CHUNK_DURATION_MS = 15000;
const CHUNK_DURATION_MS = 60000;
// const CHUNK_DURATION_MS = 10000;
const OVERLAP_DURATION_MS = 1000;
const WEBSOCKET_RESPONSE_TIMEOUT_MS = 30000;
/** Wait ~15s (3 server pings) on offline before closing socket and entering reconnect. */
const OFFLINE_GRACE_PERIOD_MS = 15000;
/** Delay before reconnecting when "online" fires so the network stack is ready (first try succeeds). */
const ONLINE_RECONNECT_DELAY_MS = 1200;
const PREFERRED_OPUS_MIME_TYPE = 'audio/webm;codecs=opus';
const ALTERNATIVE_OPUS_MIME_TYPE = 'audio/ogg;codecs=opus';
const MIN_CHUNK_SIZE_BYTES = 100;

export function useTranscriber(uiCallbacks = {}) {
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const [microphones, setMicrophones] = useState([]);
  const [activeMicrophone, setActiveMicrophone] = useState(null);
  const [volume, setVolume] = useState(0);
  const [microphoneError, setMicrophoneError] = useState(null);
  const analyserRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRefVoice = useRef(null);
  const chunkTimeoutRef = useRef(null);
  const firstChunkScheduledRef = useRef(false);
  const responseTimeoutRef = useRef(null);
  const timerRef = useRef(null);
  const recordingTimeRef = useRef(0);
  const currentRecordingIdRef = useRef(null);
  const micStreamRef = useRef(null);
  const speakerStreamRef = useRef(null);
  const startRecordingInProgressRef = useRef(false);
  const vadAnalyserRef = useRef(null); // For VAD analysis
  // Only subscribe to the specific fields we need, not recordingTime
  // This prevents re-renders every second when the timer updates
  const currentMeetingDetails = useSelector(
    state => ({
      isRecording: state.currentMeetingDetails.isRecording,
      stopped: state.currentMeetingDetails.stopped,
      manualPause: state.currentMeetingDetails.manualPause,
      fromHistoryResume: state.currentMeetingDetails.fromHistoryResume,
      paused: state.currentMeetingDetails.paused,
      parentMeetId: state.currentMeetingDetails.parentMeetId,
      meeting_id: state.currentMeetingDetails.meeting_id,
      autoPaused: state.currentMeetingDetails.autoPaused,
    }),
    (prev, next) =>
      prev.isRecording === next.isRecording &&
      prev.stopped === next.stopped &&
      prev.manualPause === next.manualPause &&
      prev.fromHistoryResume === next.fromHistoryResume &&
      prev.paused === next.paused &&
      prev.parentMeetId === next.parentMeetId &&
      prev.meeting_id === next.meeting_id &&
      prev.autoPaused === next.autoPaused
  );
  const [isMicActive, setIsMicActive] = useState(false);
  const [isSystemActive, setIsSystemActive] = useState(false);
  let reconnectAttempts = useRef(0);
  let reconnectTimer = useRef(null);
  const MAX_RETRIES = 5;
  const retryRef = useRef(0);
  const stopClicked = useRef(false);
  const pauseClicked = useRef(false);
  const isResumingRef = useRef(false); // To signal when the user is resuming
  const reconnectInProgressRef = useRef(false); // NEW FLAG
  const reconnectTimeoutsRef = useRef(new Set());
  const suppressReconnectRef = useRef(false);
  const reconnectWhenVisibleRef = useRef(false);

  const [state, setState] = useState({
    isRecordingHook: false,
    isPaused: false,
    hasTranscript: false,
    isWebSocketOpen: false,
    canGenerateMOM: false,
  });

  const stateRef = useRef(state);
  const pingWatchdogTimerRef = useRef(null); // This timer is ONLY for pings
  const stateSnapshotRef = useRef(null);
  const pingTimeoutRef = useRef(null);
  const connectWebSocketRef = useRef(null);
  const missedPingCountRef = useRef(0); // Count consecutive missed ping windows
  const resetPingWatchdogRef = useRef(null);
  const reconnectSourceRef = useRef(null); // Track who initiated reconnect
  const processingQueuedChunksRef = useRef(false); // Prevent concurrent processing
  const offlineGraceTimerRef = useRef(null); // Wait 15s on offline before closing socket
  const onlineReconnectTimerRef = useRef(null); // Delay reconnect on "online" so network is ready
  const reconnectingFromOnlineRef = useRef(false); // So onclose can schedule retry if this attempt fails
  const recordingLockReleaseRef = useRef(null); // Release function for Web Locks recording lock
  /** True after we have received an initial "connected" (or ready_to_*) status from server. */
  const serverReadyRef = useRef(false);
  /** Resolver that is waiting for the first "connected"/"ready_to_*" style message. */
  const waitForServerReadyResolveRef = useRef(null);
  /** When set, stopRecording is waiting for recording_id from server; onmessage will resolve it. */
  const waitForRecordingIdResolveRef = useRef(null);
  /** When set, stopRecording is waiting for the next message (final response/transcript); onmessage will resolve it. */
  const waitForNextMessageResolveRef = useRef(null);
  /** True after pause sends a chunk over WS; cleared when the server responds with a transcript. */
  const pauseChunkPendingRef = useRef(false);
  /** When set, resumeRecording is waiting for the pause-chunk transcript; onmessage will resolve it. */
  const waitForPauseTranscriptResolveRef = useRef(null);

  useEffect(() => {

    // This effect runs after every render, ensuring our ref
    // ALWAYS has a reference to the LATEST version of the state.
    stateSnapshotRef.current = currentMeetingDetails;
  }, [currentMeetingDetails]);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Initialize chunk queue on mount
  useEffect(() => {
    chunkQueue.initQueue().catch(() => { });
  }, []);

  /**
   * Acquire a Web Locks API lock while recording is active to reduce
   * background throttling of timers and WebSocket activity.
   * This is best-effort and silently no-ops if the API is unsupported.
   */
  const acquireRecordingLock = () => {
    try {
      if (
        typeof navigator === 'undefined' ||
        !navigator.locks ||
        typeof navigator.locks.request !== 'function'
      ) {
        return;
      }
    } catch {
      // Accessing navigator threw (e.g., during SSR) – just skip.
      return;
    }

    // If we already hold a lock, do nothing.
    if (recordingLockReleaseRef.current) {
      return;
    }

    navigator.locks
      .request('transcriber-recording', () => {
        // Hold the lock until explicitly released via the stored callback.
        return new Promise(resolve => {
          recordingLockReleaseRef.current = () => {
            recordingLockReleaseRef.current = null;
            resolve();
          };
        });
      })
      .catch(error => {
        if (!error || error.name !== 'AbortError') {
          // Failed to acquire transcriber-recording lock
        }
        recordingLockReleaseRef.current = null;
      });
  };

  /**
   * Release the Web Locks recording lock, if held.
   */
  const releaseRecordingLock = () => {
    if (recordingLockReleaseRef.current) {
      recordingLockReleaseRef.current();
      recordingLockReleaseRef.current = null;
    }
  };

  const resetAll = () => {
    // Close existing WebSocket so "Start New" doesn't leave orphaned connections.
    if (socketRef.current) {
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      try {
        if (
          socketRef.current.readyState !== WebSocket.CLOSED &&
          socketRef.current.readyState !== WebSocket.CLOSING
        ) {
          socketRef.current.close();
        }
      } catch (e) {
        // ignore
      }
      socketRef.current = null;
    }
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
      pingTimeoutRef.current = null;
    }
    if (reconnectTimeoutsRef.current?.size) {
      reconnectTimeoutsRef.current.forEach(clearTimeout);
      reconnectTimeoutsRef.current.clear();
    }
    // reset refs
    mediaRecorderRef.current = null;
    audioCtxRef.current = null;
    streamRef.current = null;
    recordedChunksRef.current = [];
    analyserRef.current = null;
    animationRef.current = null;
    audioContextRef.current = null;
    sourceRef.current = null;
    streamRefVoice.current = null;
    chunkTimeoutRef.current = null;
    firstChunkScheduledRef.current = false;
    responseTimeoutRef.current = null;
    timerRef.current = null;
    recordingTimeRef.current = 0;
    currentRecordingIdRef.current = null;
    micStreamRef.current = null;
    speakerStreamRef.current = null;
    reconnectAttempts.current = 0;
    reconnectTimer.current = null;
    retryRef.current = 0;
    stopClicked.current = false;
    pauseClicked.current = false;
    isResumingRef.current = false;
    pingWatchdogTimerRef.current = null;
    stateSnapshotRef.current = null;
    reconnectInProgressRef.current = false; // NEW FLAG
    reconnectTimeoutsRef.current = new Set();
    suppressReconnectRef.current = false;
    reconnectWhenVisibleRef.current = false;
    reconnectingFromOnlineRef.current = false;
    reconnectSourceRef.current = null;
    processingQueuedChunksRef.current = false;
    startRecordingInProgressRef.current = false;
    serverReadyRef.current = false;
    if (waitForServerReadyResolveRef.current) {
      waitForServerReadyResolveRef.current(false);
      waitForServerReadyResolveRef.current = null;
    }
    releaseRecordingLock();
    if (pingTimeoutRef.current) clearTimeout(pingTimeoutRef.current);
    pingTimeoutRef.current = null;
    if (offlineGraceTimerRef.current) {
      clearTimeout(offlineGraceTimerRef.current);
      offlineGraceTimerRef.current = null;
    }
    if (onlineReconnectTimerRef.current) {
      clearTimeout(onlineReconnectTimerRef.current);
      onlineReconnectTimerRef.current = null;
    }
    // reset state
    setMicrophones([]);
    setActiveMicrophone(null);
    setVolume(0);
    setMicrophoneError(null);
    setIsMicActive(false);
    setIsSystemActive(false);
    setState({
      isRecordingHook: false,
      isPaused: false,
      hasTranscript: false,
      isWebSocketOpen: false,
      canGenerateMOM: false,
    });
    // Clear chunk queue on reset
    chunkQueue.clearQueue().catch(() => { });
  };
  const dispatch = useDispatch();
  const store = useStore();

  // Helper to get recordingTime directly from Redux store
  // This avoids stale closure issues and ensures we always get the latest value
  const getRecordingTime = () =>
    coerceDurationToSeconds(
      store.getState().currentMeetingDetails?.recordingTime
    );

  // const resetPingWatchdog = () => {
  //   if (pingTimeoutRef.current) {
  //     clearTimeout(pingTimeoutRef.current);
  //   }
  //   pingTimeoutRef.current = setTimeout(() => {
  //     console.warn("⏱️ No ping from server in 10s → closing socket");
  //     if (socketRef.current) {
  //       // Let onclose handle reconnect logic
  //       socketRef.current.close();
  //     }
  //   }, 10000); // 10s watchdog
  // };
  // Inside your useTranscriber hook...

  const resetPingWatchdog = ({ force = false } = {}) => {
    if (document.hidden) {
      return;
    }
    // Read directly from Redux store to avoid stale closure/ref issues
    const currentReduxState = store.getState().currentMeetingDetails;
    const shouldWatchdog =
      force || currentReduxState?.isRecording || stateRef.current?.isRecordingHook;
    if (!shouldWatchdog) {
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
        pingTimeoutRef.current = null;
      }
      return;
    }
    // Clear any existing timer
    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
    }

    // We just received activity from the server → reset missed ping counter.
    missedPingCountRef.current = 0;

    // Set a new timer
    pingTimeoutRef.current = setTimeout(async () => {
      // We have not seen a ping (or any server activity that reset the watchdog)
      // within the grace window.
      missedPingCountRef.current += 1;

      const missed = missedPingCountRef.current;

      // For the first two missed windows, attempt transparent reconnect without
      // pausing the meeting. On the third consecutive miss, pause the meeting
      // and surface a clear snackbar so the user knows transcription is paused.
      const shouldHardPause = missed >= 3;

      if (shouldHardPause) {
        dispatch(
          setCurrentRecording({
            websocketConnection: false,
            paused: true,
          })
        );
        updateAppState({ isPaused: true });
        dispatch(
          openSnackbar(
            'warning',
            'Connection to Genie Transcribe is unstable. Meeting has been paused until connection is restored.'
          )
        );
      } else {
        uiCallbacks.updateStatus?.('Reconnecting...', 'info');
        dispatch(
          setCurrentRecording({
            websocketConnection: false,
          })
        );
      }

      if (!stateSnapshotRef.current?.stopped) {
        if (retryRef.current < MAX_RETRIES) {
          reconnectInProgressRef.current = true;

          retryRef.current += 1;
          const delay = Math.min(1000 * 2 ** retryRef.current, 30000);
          const isOfflineWatchdog =
            typeof navigator !== 'undefined' && !navigator.onLine;
          if (isOfflineWatchdog || retryRef.current >= 2) {
            dispatch(openSnackbar('warning', 'Failed to connect.Retrying'));
          }
          const timeoutId = setTimeout(() => {
            connectWebSocket();
            reconnectTimeoutsRef.current.delete(timeoutId);
          }, delay);

          reconnectTimeoutsRef.current.add(timeoutId);
        } else if (retryRef.current >= MAX_RETRIES) {
          dispatch(
            openSnackbar(
              'error',
              'Connection issue: Unable to establish a secure connection to the Genie Transcribe. Please check your internet and try again',
              true
            )
          );
          dispatch(
            setCurrentRecording({
              audioControlDisabled: true,
              isRecording: false,
            })
          );
        }
      }

      // Now, close the socket. Its `onclose` handler will see reconnectInProgressRef and skip duplicate logic.
      if (socketRef.current) {
        socketRef.current.close();
      }
    }, WEBSOCKET_RESPONSE_TIMEOUT_MS); // watchdog interval
  };

  useEffect(() => {
    resetPingWatchdogRef.current = resetPingWatchdog;
  }, [resetPingWatchdog]);

  const updateAppState = patch => setState(prev => ({ ...prev, ...patch }));

  useEffect(() => {
    if (!activeMicrophone) return;

    navigator.mediaDevices
      .getUserMedia({ audio: { deviceId: activeMicrophone.deviceId } })
      .then(stream => {
        streamRefVoice.current = stream;
        audioContextRef.current = new AudioContext();
        sourceRef.current =
          audioContextRef.current.createMediaStreamSource(stream);
        analyserRef.current = audioContextRef.current.createAnalyser();
        sourceRef.current.connect(analyserRef.current);
        analyserRef.current.fftSize = 256;

        const bufferLength = analyserRef.current.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);

        const updateVolume = () => {
          analyserRef.current?.getByteFrequencyData(dataArray);
          const avg = dataArray.reduce((a, b) => a + b) / bufferLength;
          setVolume(Math.min(100, Math.max(0, (avg / 256) * 100)));
          animationRef.current = requestAnimationFrame(updateVolume);
        };

        updateVolume();
      })
      .catch(err => {
        setMicrophoneError(err);
      });

    return () => {
      cancelAnimationFrame(animationRef.current);
      streamRefVoice.current?.getTracks().forEach(t => t.stop());
      audioContextRef.current?.close();
    };
  }, [activeMicrophone]);

  const handlePageExit = async () => {
    await stopRecording('stop_unload');
  };

  useEffect(() => {
    window.addEventListener('pagehide', handlePageExit);
    return () => {
      window.removeEventListener('pagehide', handlePageExit);
    };
  }, [currentMeetingDetails?.isRecording]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (pingTimeoutRef.current) {
          clearTimeout(pingTimeoutRef.current);
          pingTimeoutRef.current = null;
        }
        return;
      }

      if (reconnectWhenVisibleRef.current) {
        reconnectWhenVisibleRef.current = false;
        const { manualPause, autoPaused, stopped } =
          stateSnapshotRef.current || {};

        if (
          !manualPause &&
          !autoPaused &&
          !stopped &&
          socketRef.current?.readyState !== WebSocket.OPEN
        ) {
          connectWebSocketRef.current?.();
        }
        return;
      }

      if (socketRef.current?.readyState === WebSocket.OPEN) {
        resetPingWatchdogRef.current?.();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Handle online/offline events to detect internet reconnection
  useEffect(() => {
    const handleOnline = () => {
      // Cancel offline grace period so we don't close socket after coming back online
      if (offlineGraceTimerRef.current) {
        clearTimeout(offlineGraceTimerRef.current);
        offlineGraceTimerRef.current = null;
      }
      // Cancel any pending delayed reconnect (e.g. duplicate online events)
      if (onlineReconnectTimerRef.current) {
        clearTimeout(onlineReconnectTimerRef.current);
        onlineReconnectTimerRef.current = null;
      }

      // Read current state directly from store to avoid stale closures
      const currentState = store.getState().currentMeetingDetails;
      const { manualPause, autoPaused, stopped, isRecording } = currentState || {};

      // Only reconnect if:
      // 1. We're in a recording state
      // 2. Not manually paused or auto-paused
      // 3. Not stopped
      // 4. WebSocket is not already open
      if (
        isRecording &&
        !manualPause &&
        !autoPaused &&
        !stopped &&
        socketRef.current?.readyState !== WebSocket.OPEN
      ) {
        // Reset retry counter to allow fresh reconnection attempts
        retryRef.current = 0;

        // Clear any existing reconnect timeouts
        reconnectTimeoutsRef.current.forEach(clearTimeout);
        reconnectTimeoutsRef.current.clear();

        // Set reconnect flag so that onopen handler knows to auto-resume
        reconnectInProgressRef.current = true;

        uiCallbacks.updateStatus?.('Internet connection restored. Reconnecting...', 'info');

        // Delay reconnect so the network stack is ready after "online" / no throttling.
        // Without this, the first attempt often fails and only the second try works.
        onlineReconnectTimerRef.current = setTimeout(() => {
          onlineReconnectTimerRef.current = null;
          reconnectingFromOnlineRef.current = true;
          connectWebSocketRef.current?.();
        }, ONLINE_RECONNECT_DELAY_MS);
      }
    };

    const handleOffline = () => {
      // Cancel delayed reconnect when going offline
      if (onlineReconnectTimerRef.current) {
        clearTimeout(onlineReconnectTimerRef.current);
        onlineReconnectTimerRef.current = null;
      }

      const currentState = store.getState().currentMeetingDetails;
      const { isRecording, manualPause, autoPaused, stopped } =
        currentState || {};

      if (!isRecording || manualPause || autoPaused || stopped) {
        return;
      }

      // Clear any existing grace timer (e.g. multiple offline events)
      if (offlineGraceTimerRef.current) {
        clearTimeout(offlineGraceTimerRef.current);
        offlineGraceTimerRef.current = null;
      }

      // Wait for next ~3 ping/pong cycles (15s) before treating as real disconnection.
      // This avoids immediate reconnect on brief network blips and distinguishes
      // server-side vs client-side drop.
      uiCallbacks.updateStatus?.(
        'Internet connection lost. Checking connection...',
        'warning'
      );

      offlineGraceTimerRef.current = setTimeout(async () => {
        offlineGraceTimerRef.current = null;
        // Flush current recorder segment so the last chunk is queued and sent when back online.
        try {
          await flushCurrentRecorderSegment();
        } catch (e) {
          // Ignore flush errors
        }
        // Still offline after grace period: treat as real disconnection.
        uiCallbacks.updateStatus?.(
          'Internet connection lost. Waiting for reconnection...',
          'warning'
        );
        dispatch(
          setCurrentRecording({
            websocketConnection: false,
            paused: true,
          })
        );
        updateAppState({ isPaused: true });
        // Auto-pause: stop the meeting timer when entering reconnect state
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        uiCallbacks.updateTimer?.(recordingTimeRef.current);
        if (
          socketRef.current &&
          (socketRef.current.readyState === WebSocket.OPEN ||
            socketRef.current.readyState === WebSocket.CONNECTING)
        ) {
          try {
            socketRef.current.close();
          } catch {
            // Ignore close errors during offline transition
          }
        }
      }, OFFLINE_GRACE_PERIOD_MS);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      if (offlineGraceTimerRef.current) {
        clearTimeout(offlineGraceTimerRef.current);
        offlineGraceTimerRef.current = null;
      }
      if (onlineReconnectTimerRef.current) {
        clearTimeout(onlineReconnectTimerRef.current);
        onlineReconnectTimerRef.current = null;
      }
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (currentMeetingDetails.isRecording) {
      // Use getRecordingTime() to read directly from Redux store
      // since recordingTime is not in the selector
      recordingTimeRef.current = getRecordingTime();
    }
  }, [currentMeetingDetails.isRecording]);

  useEffect(() => {
    if (!currentMeetingDetails?.stopped) return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [currentMeetingDetails?.stopped]);

  const selectMicrophone = async deviceId => {
    const selected = microphones.find(m => m.deviceId === deviceId);
    if (!selected) return;

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: selected.deviceId } },
      });

      // Replace old stream if you're already recording
      if (streamRefVoice.current) {
        streamRefVoice.current.getTracks().forEach(track => track.stop());
      }

      streamRefVoice.current = newStream;
      setActiveMicrophone(selected);

      // Also: update any component/hook using this stream
      // Example: setMicStream(newStream) or dispatch to global store
    } catch (error) {
      alert('Failed to switch microphone: ' + error.message);
    }
  };

  // const selectMicrophone = (deviceId) => {
  //   const selected = microphones.find((m) => m.deviceId === deviceId);
  //   if (selected) setActiveMicrophone(selected);
  // };
  const getToken = () => localStorage.getItem('transcriptSession');
  //

  const attemptReconnect = () => {
    if (reconnectAttempts.current >= 5) {
      uiCallbacks.updateStatus?.(
        'Failed to reconnect after multiple attempts.',
        'error'
      );
      return;
    }

    const delay = Math.min(
      1000 * Math.pow(2, reconnectAttempts.current),
      30000
    );
    reconnectAttempts.current++;

    reconnectTimer.current = setTimeout(() => {
      connectWebSocket();
    }, delay);
  };

  const pauseMeeting = () => {
    updateAppState({ isPaused: true });
    stopRecording(); // stop mic/speaker streams but keep meeting state
    uiCallbacks.updateStatus?.(
      'Meeting paused due to lost connection.',
      'warning'
    );
  };

  const resumeMeeting = async () => {
    try {
      updateAppState({ isPaused: false });
      await startRecording({ useMic: true, useSpeaker: true });
      uiCallbacks.updateStatus?.(
        'Meeting resumed after reconnection.',
        'success'
      );
    } catch (err) {
      uiCallbacks.updateStatus?.('Failed to resume meeting.', 'error');
    }
  };

  /** True if we can send a chunk now (socket open and online). When false, chunks must be queued. */
  const canSendChunkNow = () =>
    socketRef.current?.readyState === WebSocket.OPEN &&
    typeof navigator !== 'undefined' &&
    navigator.onLine;

  /**
   * Process queued chunks and send them via WebSocket in order.
   * After reconnection, sends all stored chunks so no audio is lost (e.g. chunk that
   * failed when user was offline at first 60s boundary). Includes guard to prevent
   * concurrent processing and double-sending.
   */
  const processQueuedChunks = async () => {
    if (processingQueuedChunksRef.current) {
      return;
    }

    processingQueuedChunksRef.current = true;

    try {
      const meetingId = currentRecordingIdRef.current || currentMeetingDetails?.meeting_id;
      const queuedChunks = await chunkQueue.getAllChunks(meetingId);

      if (queuedChunks.length === 0) {
        processingQueuedChunksRef.current = false;
        return;
      }

      if (socketRef.current?.readyState !== WebSocket.OPEN) {
        uiCallbacks.updateStatus?.(
          'WebSocket not ready. Queued chunks will be sent when connection is established.',
          'warning'
        );
        processingQueuedChunksRef.current = false;
        return;
      }

      uiCallbacks.updateStatus?.(
        `Sending ${queuedChunks.length} queued chunk(s) after reconnection...`,
        'info'
      );

      let sent = 0;
      for (const chunk of queuedChunks) {
        if (socketRef.current?.readyState !== WebSocket.OPEN) break;
        try {
          await chunkQueue.removeChunk(chunk.id);
          socketRef.current.send(chunk.blob);
          sent += 1;
        } catch (error) {
          try {
            await chunkQueue.addChunk(chunk.blob, {
              mimeType: chunk.mimeType || 'audio/webm',
              meetingId: meetingId,
            });
          } catch (reAddError) {
            // Ignore re-add error
          }
          uiCallbacks.updateStatus?.(
            `Sent ${sent} of ${queuedChunks.length} queued chunks. Rest will be retried later.`,
            'warning'
          );
          break;
        }
      }

      if (sent > 0) {
        uiCallbacks.updateStatus?.(
          sent === queuedChunks.length
            ? 'Successfully sent all queued audio after reconnection.'
            : `Sent ${sent} queued chunk(s). Remaining will retry when connection is ready.`,
          'success'
        );
        dispatch(
          openSnackbar(
            'success',
            sent === queuedChunks.length
              ? 'Successfully sent all queued audio after reconnection.'
              : `Sent ${sent} queued chunk(s) after reconnection.`
          )
        );
      }
    } catch (error) {
      uiCallbacks.updateStatus?.(
        'Error processing queued chunks. They will be retried later.',
        'error'
      );
    } finally {
      processingQueuedChunksRef.current = false;
    }
  };

  const connectWebSocket = () => {
    const token = getToken();

    if (!token) return;

    // New transport attempt: clear stale flag so a replacement socket failing before
    // onopen does not strand retries (onclose would otherwise return early).
    reconnectInProgressRef.current = false;

    // Ensure only one WebSocket: close any existing connection before opening a new one.
    if (socketRef.current) {
      const existing = socketRef.current;
      socketRef.current = null;
      existing.onclose = null;
      existing.onerror = null;
      try {
        if (
          existing.readyState !== WebSocket.CLOSED &&
          existing.readyState !== WebSocket.CLOSING
        ) {
          existing.close();
        }
      } catch (e) {
        // ignore
      }
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
        pingTimeoutRef.current = null;
      }
      if (reconnectTimeoutsRef.current?.size) {
        reconnectTimeoutsRef.current.forEach(clearTimeout);
        reconnectTimeoutsRef.current.clear();
      }
    }

    const url = API_URL?.split('//')[1]; // Get base URL without /api
    let wsUrl = `${window.location.protocol === 'https:' ? 'wss' : 'wss'
      }://${url}/ws?token=${encodeURIComponent(token)}`;

    // Read current state directly from store to avoid stale closures
    const currentState = store.getState().currentMeetingDetails;
    const parentMeetId = currentState?.parentMeetId;
    const fromHistoryResume = currentState?.fromHistoryResume;
    const meetingId = currentState?.meeting_id || currentRecordingIdRef.current;
    const isActiveMeeting =
      currentState?.isRecording && !currentState?.stopped && !!meetingId;

    // Per WebSocket Client Integration Guide:
    // - resume: when new meeting is created from an existing completed meeting (history).
    // - play: when an ongoing meeting (with an existing recording_id) is being continued
    //   after a pause or reconnect – this should NOT create a new meeting.
    //
    // History resume: only when we don't yet have a child recording_id.
    if (parentMeetId && fromHistoryResume && !meetingId) {
      wsUrl += `&resume=${parentMeetId}`;
    }
    // Any reconnect/continue while a meeting is active (explicit Resume or auto reconnect):
    else if (isActiveMeeting) {
      wsUrl += `&play=${meetingId}`;
    }

    const socket = new WebSocket(wsUrl);
    socketRef.current = socket;

    socket.onopen = async () => {
      reconnectWhenVisibleRef.current = false;
      reconnectingFromOnlineRef.current = false;
      uiCallbacks.updateStatus?.('Connected to Genie Transcribe.', 'success');
      resetPingWatchdog({ force: true });

      // Base WebSocket is open – but the transcription session itself is not
      // considered ready until we get a server-side "connected"/"ready_to_*"
      // control message. Mark as not ready here; onmessage will flip this.
      serverReadyRef.current = false;

      // Send keepalive immediately so server/proxy does not close the connection
      // before the first audio chunk (which is sent at 60s or on pause/stop).
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        try {
          socketRef.current.send(
            JSON.stringify({
              type: 'control',
              action: 'keepalive',
              duration: getRecordingTime(),
            })
          );
        } catch (e) {
          // ignore
        }
      }

      const shouldAutoResume = reconnectInProgressRef.current;
      const wasPausedDueToConnectionLoss =
        stateRef.current.isPaused &&
        !stateSnapshotRef.current?.manualPause &&
        !stateSnapshotRef.current?.autoPaused &&
        !stateSnapshotRef.current?.stopped;

      retryRef.current = 0;
      reconnectInProgressRef.current = false;
      reconnectTimeoutsRef.current.forEach(clearTimeout);
      reconnectTimeoutsRef.current.clear();
      reconnectSourceRef.current = null;

      // Always set websocketConnection to true when connection is established
      dispatch(
        setCurrentRecording({
          websocketConnection: true,
        })
      );

      // Run auto-resume FIRST (while stateRef.current.isPaused is still true) so
      // resumeRecording runs and restarts the timer + MediaRecorder. If we cleared
      // paused state before this check, the condition would be false and the timer
      // would never resume (e.g. on mobile after "Failed to connect.Retrying").
      if (
        shouldAutoResume &&
        stateRef.current.isPaused &&
        !stateSnapshotRef.current?.manualPause &&
        !stateSnapshotRef.current?.autoPaused &&
        !stateSnapshotRef.current?.fromHistoryResume &&
        !isResumingRef.current
      ) {
        resumeRecording(true);
        dispatch(
          openSnackbar(
            'success',
            'Connection restored. The meeting is continuing.'
          )
        );
      } else if (wasPausedDueToConnectionLoss) {
        // Reconnected but didn't run full auto-resume (e.g. stream null on mobile,
        // or shouldAutoResume was false for some edge-case reason).
        // Clear paused state and restart timer + MediaRecorder so recording actually continues.
        dispatch(
          setCurrentRecording({
            paused: false,
            manualPause: false,
            autoPaused: false,
          })
        );
        updateAppState({ isPaused: false });
        recordingTimeRef.current = getRecordingTime();
        resumeTimer();
        // Also restart the MediaRecorder if the audio stream is still alive,
        // so audio capture actually resumes (not just the timer).
        if (streamRef.current) {
          try {
            await startNewRecorderSegment(streamRef.current);
          } catch (err) {
            // Failed to restart MediaRecorder in fallback resume
          }
        }
      }

      reconnectAttempts.current = 0;
      retryRef.current = 0; // reset retries on success

      // Process any queued chunks after connection is established
      // Use setTimeout to ensure WebSocket is fully ready
      setTimeout(() => {
        processQueuedChunks();
      }, 500);

      if (isResumingRef.current) {
        isResumingRef.current = false;
      }

      // Read fromHistoryResume directly from store to avoid stale closure
      const currentState = store.getState().currentMeetingDetails;
      const isFromHistoryResume = currentState?.fromHistoryResume;

      if (isFromHistoryResume) {
        // Prepare the recording time ref so startRecording/startTimer can
        // resume from the correct position, but do NOT start the timer or
        // set isRecording yet. Those are deferred to startRecording so the
        // timer only runs when the mic is successfully acquired.
        recordingTimeRef.current = getRecordingTime();

        dispatch(
          setCurrentRecording({
            stopped: false,
            paused: false,
          })
        );

        // Clear fromHistoryResume after successful connection to prevent
        // it from affecting subsequent resume operations
        dispatch(
          setCurrentRecording({
            fromHistoryResume: false,
          })
        );

        // Restart ping watchdog after state updates have been applied
        // Use queueMicrotask to ensure React state updates have propagated
        queueMicrotask(() => {
          // Force start the watchdog since we know we're in recording mode
          if (pingTimeoutRef.current) {
            clearTimeout(pingTimeoutRef.current);
          }
          pingTimeoutRef.current = setTimeout(() => {
            uiCallbacks.updateStatus?.('Connection lost. Reconnecting...', 'warning');
            dispatch(
              setCurrentRecording({
                websocketConnection: false,
              })
            );
            if (socketRef.current) {
              socketRef.current.close();
            }
          }, WEBSOCKET_RESPONSE_TIMEOUT_MS);
        });
      }
    };

    socket.onmessage = event => {
      resetPingWatchdog();

      if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);

      if (typeof event.data !== 'string') {
        return;
      }

      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        if (
          event.data.includes('"action":"ping"') ||
          event.data.includes('"action":"pong"')
        ) {
          return;
        }
        uiCallbacks.addTranscript?.('Invalid message: ' + event.data, true);
        return;
      }

      if (data.action === 'ping') {
        socketRef.current?.send(JSON.stringify({ action: 'pong' }));
        return;
      }

      // Connection / setup states from server (connected / ready_to_* / recording_started / resumed / paused / warning).
      if (data.status === 'connected') {
        // New session connected – signal any waiter that server-side session is ready.
        serverReadyRef.current = true;
        if (waitForServerReadyResolveRef.current) {
          waitForServerReadyResolveRef.current(true);
          waitForServerReadyResolveRef.current = null;
        }
      }

      if (data.status === 'ready_to_resume') {
        // Server is ready to resume an existing recording. Only seed duration from server when
        // resuming from history; for same-session resume (e.g. user re-enabled mic in PiP), keep
        // our local cumulative duration so the timer doesn't jump back.
        const fromHistory = stateSnapshotRef.current?.fromHistoryResume;
        if (fromHistory && typeof data.start_duration === 'number' && Number.isFinite(data.start_duration)) {
          recordingTimeRef.current = data.start_duration;
          dispatch(
            setCurrentRecording({
              parentMeetId: data.parent_id ?? stateSnapshotRef.current?.parentMeetId,
              recordingTime: data.start_duration,
            })
          );
        } else if (data.parent_id !== undefined || stateSnapshotRef.current?.parentMeetId) {
          dispatch(
            setCurrentRecording({
              parentMeetId: data.parent_id ?? stateSnapshotRef.current?.parentMeetId,
            })
          );
        }
        // Server is ready to resume – treat as a successful handshake.
        serverReadyRef.current = true;
        if (waitForServerReadyResolveRef.current) {
          waitForServerReadyResolveRef.current(true);
          waitForServerReadyResolveRef.current = null;
        }
      }

      if (data.status === 'ready_to_play') {
        // Server is ready to play/continue an existing recording. Only apply start_duration when
        // resuming from history; for same-session resume (user re-enabled mic), preserve local
        // cumulative duration so the timer doesn't reset (e.g. from 47s back to 21s).
        const fromHistory = stateSnapshotRef.current?.fromHistoryResume;
        const localDuration = getRecordingTime();
        const useServerDuration =
          fromHistory || !Number.isFinite(localDuration) || localDuration <= 0;

        if (data.recording_id) {
          currentRecordingIdRef.current = data.recording_id;
        }

        if (useServerDuration && typeof data.start_duration === 'number' && Number.isFinite(data.start_duration)) {
          recordingTimeRef.current = data.start_duration;
          dispatch(
            setCurrentRecording({
              meeting_id: data.recording_id ?? currentRecordingIdRef.current,
              recordingTime: data.start_duration,
            })
          );
        } else {
          if (Number.isFinite(localDuration) && localDuration > 0) {
            recordingTimeRef.current = localDuration;
          }
          dispatch(
            setCurrentRecording({
              meeting_id: data.recording_id ?? currentRecordingIdRef.current,
            })
          );
        }

        // Server is ready to continue an existing recording – handshake complete.
        serverReadyRef.current = true;
        if (waitForServerReadyResolveRef.current) {
          waitForServerReadyResolveRef.current(true);
          waitForServerReadyResolveRef.current = null;
        }
      }

      if (data.status === 'recording_started' || data.status === 'resumed') {
        // Deferred recording creation announcement – ensure recording_id is tracked.
        if (data.recording_id) {
          currentRecordingIdRef.current = data.recording_id;
          dispatch(setCurrentRecording({ meeting_id: data.recording_id }));
        }

        // In some flows the first positive acknowledgement we see is
        // "recording_started" or "resumed". Treat that as a successful handshake.
        serverReadyRef.current = true;
        if (waitForServerReadyResolveRef.current) {
          waitForServerReadyResolveRef.current(true);
          waitForServerReadyResolveRef.current = null;
        }
      }

      if (data.status === 'paused') {
        // Pause acknowledgement – server will close the socket afterwards.
        dispatch(
          setCurrentRecording({
            paused: true,
            manualPause: stateSnapshotRef.current?.manualPause || false,
            autoPaused: stateSnapshotRef.current?.autoPaused || false,
          })
        );
        updateAppState({ isPaused: true });
      }

      if (data.status === 'warning') {
        // Non-fatal warnings such as CODEC_DECODE_FAILED / CHUNK_TOO_LONG.
        if (data.message) {
          uiCallbacks.updateStatus?.(data.message, 'warning');
          dispatch(openSnackbar('warning', data.message));
        }
        // Keep socket open and continue streaming.
      }

      // On server error, differentiate between fatal and transient errors so that
      // third-party/chunk-level failures do not split the meeting or create a new one.
      if (data.status === 'error') {
        const rawCode = (data.error_code || data.code || '').toString();
        const rawMsg = (data.message || data.error || '').toString();
        const hasStructuredCode = !!rawCode;
        const codeUpper = rawCode.toUpperCase();
        const normalizedMessage = rawMsg.trim().replace(/\.+$/, '').toLowerCase();

        // Error codes that can be treated as transient / non‑fatal (meeting keeps running).
        const TRANSIENT_ERROR_CODES = ['CHUNK_ERROR', 'CHUNK_TOO_LONG', 'THIRD_PARTY_ERROR'];

        const isTransientCode =
          hasStructuredCode && TRANSIENT_ERROR_CODES.includes(codeUpper);

        if (isTransientCode) {
          const warnMessage =
            rawMsg || 'Temporary transcription error occurred. Meeting will continue.';
          uiCallbacks.updateStatus?.(warnMessage, 'warning');
          dispatch(openSnackbar('warning', warnMessage));
          return;
        }

        // Backend rejects the active play session; this meeting cannot continue safely.
        // Stop immediately instead of entering paused/reconnect state.
        const isPlayAccessDeniedError =
          normalizedMessage === 'recording to play not found or access denied';
        if (isPlayAccessDeniedError) {
          suppressReconnectRef.current = true;
          dispatch(
            setCurrentRecording({
              resumeWaitingForServer: false,
              stopped: true,
              isRecording: false,
              isStarted: false,
              isFinalizing: false,
              paused: false,
              manualPause: false,
              autoPaused: false,
              websocketConnection: false,
            })
          );
          updateAppState({ isPaused: false, isRecordingHook: false });
          stopRecording('stop_unload').catch(() => {
            // Ignore cleanup errors during forced stop.
          });
          dispatch(
            openSnackbar(
              'error',
              rawMsg || 'Recording to play not found or access denied.'
            )
          );
          return;
        }

        // All other error codes (including ASR_FAILED) or missing code are treated as fatal.
        // In this case there is no point in showing a "waiting" loader or attempting
        // auto‑reconnect – simply pause the meeting and stop the timer.

        // Ensure any "resume waiting" spinner or reconnect state is cleared so that
        // resuming after a fatal error does NOT show the waiting loader.
        dispatch(
          setCurrentRecording({
            resumeWaitingForServer: false,
            paused: true,
            manualPause: false,
            autoPaused: true,
            websocketConnection: false,
          })
        );

        // Stop the local timer so the counter does not keep running after a fatal error.
        stopTimer();

        // Stop MediaRecorder if it is still recording so no further chunks are sent.
        if (
          mediaRecorderRef.current &&
          mediaRecorderRef.current.state === 'recording'
        ) {
          try {
            mediaRecorderRef.current.stop();
          } catch {
            // Ignore recorder stop errors
          }
        }

        // Prevent any reconnect chain from starting from onclose.
        suppressReconnectRef.current = true;
        socketRef.current?.close();

        updateAppState({ isPaused: true });

        dispatch(
          openSnackbar(
            'error',
            rawMsg ||
              'Meeting has been paused due to an unexpected transcription error.'
          )
        );
        return;
      }

      if (data.status === 'silence_detected') {
        const snapshot = stateSnapshotRef.current || {};
        const noRecordingId = !currentRecordingIdRef.current;
        // When no recording ID, set flag and resolve wait refs even if user already clicked stop
        // (snapshot.stopped can be true by then), so loader ends and snackbar shows immediately.
        if (noRecordingId) {
          dispatch(setCurrentRecording({ noRecordingExpected: true }));
          if (waitForRecordingIdResolveRef.current) {
            waitForRecordingIdResolveRef.current(null);
            waitForRecordingIdResolveRef.current = null;
          }
          if (waitForNextMessageResolveRef.current) {
            waitForNextMessageResolveRef.current();
            waitForNextMessageResolveRef.current = null;
          }
        }
        // When we have a recording ID, still resolve "next message" so stop flow completes and loader stops.
        if (waitForNextMessageResolveRef.current) {
          waitForNextMessageResolveRef.current();
          waitForNextMessageResolveRef.current = null;
        }
        if (!snapshot.paused && !snapshot.autoPaused && !snapshot.stopped) {
          const message =
            data.message ||
            'No discernible audio detected.';
          uiCallbacks.updateStatus?.(message, 'warning');
          if (noRecordingId) {
            // Snackbar will be shown by handleStopClick finally; avoid duplicate here.
          } else {
            dispatch(openSnackbar('warning', message));
          }
        }
        return;
      }

      // When server reports no usable speech, no recording/meeting ID will be generated.
      // UI will show snackbar without loader and not wait for WebSocket on stop.
      const NO_RECORDING_STATUSES = [
        'LOW_SIGNAL_AUDIO',
        'NO_VOICE_ACTIVITY',
        'NO_CLEAR_SPEECH',
        'NO_SPEECH_DETECTED',
      ];
      const statusStr = (data.status || '').toUpperCase();
      const messageStr = (data.message || '').toUpperCase();
      const isNoRecordingScenario =
        NO_RECORDING_STATUSES.some(s => statusStr === s || messageStr.includes(s));
      if (isNoRecordingScenario) {
        dispatch(setCurrentRecording({ noRecordingExpected: true }));
        return;
      }

      if (data.recording_id) {
        currentRecordingIdRef.current = data.recording_id;
        dispatch(setCurrentRecording({ meeting_id: data.recording_id }));
        if (waitForRecordingIdResolveRef.current) {
          waitForRecordingIdResolveRef.current(data.recording_id);
          waitForRecordingIdResolveRef.current = null;
        }
      }

      const transcriptText =
        data.transcript ||
        (data.status === 'transcript_chunk' ? data.transcript : '');

      if (transcriptText && transcriptText.trim()) {
        const cleanedTranscript = transcriptText
          .replace(/\|/g, '')
          .replace(/```/g, '')
          .trim();
        uiCallbacks.addTranscript?.(cleanedTranscript, data.is_final);
        updateAppState({ hasTranscript: true });
      } else if (data.error) {
        uiCallbacks.addTranscript?.(`Server Error: ${data.error}`, true);
      }

      // Resolve pause-chunk transcript wait only when actual transcript text arrives
      // (recording_id often arrives in a separate earlier message with no transcript)
      if (pauseChunkPendingRef.current && transcriptText?.trim()) {
        pauseChunkPendingRef.current = false;
        if (waitForPauseTranscriptResolveRef.current) {
          waitForPauseTranscriptResolveRef.current();
          waitForPauseTranscriptResolveRef.current = null;
        }
      }

      // After processing this message, signal that we received the response (for stop flow).
      if (waitForNextMessageResolveRef.current) {
        waitForNextMessageResolveRef.current();
        waitForNextMessageResolveRef.current = null;
      }
    };

    socket.onclose = (event) => {
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
        pingTimeoutRef.current = null;
      }
      const { manualPause, paused, stopped, autoPaused } =
        stateSnapshotRef.current || {};

      if (
        manualPause ||
        autoPaused ||
        stopped ||
        stopClicked.current ||
        pauseClicked.current
      ) {
        return;
      }

      if (suppressReconnectRef.current) {
        return;
      }
      const isTabHidden = document.hidden;
      if (isTabHidden) {
        // Remember that we should ensure a connection once the tab is visible again,
        // but still allow the normal reconnect logic below to run in the background.
        reconnectWhenVisibleRef.current = true;
      }
      // If this close is from the "online" reconnect attempt that failed, allow retry logic below
      // so we schedule another attempt (first try after "online" can still fail if network not ready).
      if (reconnectInProgressRef.current && reconnectingFromOnlineRef.current) {
        reconnectingFromOnlineRef.current = false;
        reconnectInProgressRef.current = false;
      } else if (reconnectInProgressRef.current) {
        return;
      }

      // Decide whether to show "Failed to connect.Retrying" — only when there is likely
      // a real connection/internet problem, not e.g. server idle timeout (~13s) when
      // no audio chunk has been sent yet (chunks go at 60s or on pause/stop).
      const closeCode = event?.code;
      const isOffline =
        typeof navigator !== 'undefined' && !navigator.onLine;
      const isAbnormalClose =
        closeCode === 1006 || closeCode === 1011; // 1006 = abnormal, 1011 = server error
      const retryCountBefore = retryRef.current;

      dispatch(
        setCurrentRecording({
          websocketConnection: false,
          paused: true,
        })
      );
      updateAppState({ isPaused: true });
      if (retryRef.current < MAX_RETRIES) {
        reconnectInProgressRef.current = true; // mark reconnect chain started

        retryRef.current += 1;
        const delay = Math.min(1000 * 2 ** retryRef.current, 30000);
        // Seamless reconnect: do NOT pause the meeting. Keep timer and MediaRecorder
        // running so the meeting continues without interruption; reconnect in background.
        if (!isTabHidden) {
          uiCallbacks.updateStatus?.('Reconnecting...', 'info');
          const shouldShowConnectionError =
            isOffline || isAbnormalClose || retryCountBefore >= 1;
          if (shouldShowConnectionError) {
            dispatch(openSnackbar('warning', 'Failed to connect.Retrying'));
          }
        }
        const timeoutId = setTimeout(() => {
          connectWebSocket();
          reconnectTimeoutsRef.current.delete(timeoutId);
        }, delay);

        reconnectTimeoutsRef.current.add(timeoutId);
      } else if (retryRef.current >= MAX_RETRIES) {
        dispatch(
          openSnackbar(
            'error',
            'Connection issue: Unable to establish a secure connection to the Genie Transcribe. Please check your internet and try again',
            true
          )
        );
        dispatch(
          setCurrentRecording({
            audioControlDisabled: true,
            isRecording: false,
          })
        );
      }
      // }
    };

    socket.onerror = () => {
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
        pingTimeoutRef.current = null;
      }
      dispatch(
        setCurrentRecording({
          websocketConnection: false,
        })
      );
      socket?.close();
    };
  };

  useEffect(() => {
    connectWebSocketRef.current = connectWebSocket;
  }, [connectWebSocket]);

  /**
   * Wait until the WebSocket is OPEN at the transport level and the server
   * has acknowledged the session with a control message
   * (`status: "connected" | "ready_to_resume" | "ready_to_play" | "recording_started" | "resumed"`).
   * This prevents the meeting UI from "starting" while the backend has already
   * rejected or failed the session.
   */
  const waitForServerReady = async (timeoutMs = 10000) => {
    if (socketRef.current?.readyState === WebSocket.OPEN && serverReadyRef.current) {
      return true;
    }

    return new Promise((resolve, reject) => {
      const startSocket = socketRef.current;

      // If another waiter is already installed, just piggy‑back on it.
      if (waitForServerReadyResolveRef.current) {
        const existingResolver = waitForServerReadyResolveRef.current;
        waitForServerReadyResolveRef.current = value => {
          existingResolver(value);
          resolve(value);
        };
      } else {
        waitForServerReadyResolveRef.current = resolve;
      }

      const timeoutId = setTimeout(() => {
        if (waitForServerReadyResolveRef.current === resolve) {
          waitForServerReadyResolveRef.current = null;
        }
        reject(new Error('Server readiness timeout'));
      }, timeoutMs);

      // If socket closes before we get a ready message, fail fast.
      const originalOnClose = startSocket?.onclose;
      if (startSocket) {
        startSocket.onclose = evt => {
          clearTimeout(timeoutId);
          if (waitForServerReadyResolveRef.current === resolve) {
            waitForServerReadyResolveRef.current = null;
          }
          reject(new Error('WebSocket closed before server was ready'));
          if (typeof originalOnClose === 'function') {
            originalOnClose(evt);
          }
        };
      }
    });
  };

  const ensureWebSocketConnected = async (timeoutMs = 5000) => {
    return new Promise((resolve, reject) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        return resolve(true);
      }

      connectWebSocket();

      const checkInterval = setInterval(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
          clearInterval(checkInterval);
          clearTimeout(timeout);
          resolve(true);
        }
      }, 200);

      const timeout = setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('WebSocket connection timeout'));
      }, timeoutMs);
    });
  };

  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (!Number.isFinite(recordingTimeRef.current)) {
        recordingTimeRef.current = 0;
      }
      recordingTimeRef.current += 1;
      uiCallbacks.updateTimer?.(recordingTimeRef.current);
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    uiCallbacks.updateTimer?.(recordingTimeRef.current); // send the last known time

    // recordingTimeRef.current = 0;
    // uiCallbacks.updateTimer?.(0);
  };

  const resetTimer = () => {
    recordingTimeRef.current = 0;
    uiCallbacks.updateTimer?.(0);
  };

  const pauseTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const resumeTimer = () => {
    startTimer();
  };

  /**
   * Stop the current recorder segment (if any) and wait for its onstop to complete,
   * so the blob is either sent or queued. Call this before starting a new segment
   * (e.g. on resume after reconnect) and when going offline so the last chunk is not lost.
   */
  const flushCurrentRecorderSegment = async () => {
    if (chunkTimeoutRef.current) {
      clearTimeout(chunkTimeoutRef.current);
      chunkTimeoutRef.current = null;
    }
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    const stopPromise = new Promise(resolve => {
      recorder.addEventListener('stop', () => resolve(), { once: true });
    });
    try {
      recorder.requestData();
      recorder.stop();
    } catch (e) {
      // Recorder may already be stopped
      return;
    }
    await stopPromise;
    // Allow async onstop handler (send/queue blob) to complete
    await new Promise(r => setTimeout(r, 250));
  };

  const startNewRecorderSegment = async stream => {
    // When starting a new recording segment, attempt to acquire a Web Locks
    // recording lock so the browser is less likely to heavily throttle this tab
    // while transcription is ongoing.
    acquireRecordingLock();

    // Flush any existing segment first so its blob is sent or queued (fixes lost
    // chunk when user was offline and then reconnects, or when resuming).
    await flushCurrentRecorderSegment();

    recordedChunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported(PREFERRED_OPUS_MIME_TYPE)
      ? PREFERRED_OPUS_MIME_TYPE
      : MediaRecorder.isTypeSupported(ALTERNATIVE_OPUS_MIME_TYPE)
        ? ALTERNATIVE_OPUS_MIME_TYPE
        : '';

    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = e => {
      if (e.data && e.data.size >= MIN_CHUNK_SIZE_BYTES) {
        recordedChunksRef.current.push(e.data);
      }
    };

    recorder.onstop = async () => {

      const blob = new Blob(recordedChunksRef.current, { type: mimeType });

      if (canSendChunkNow()) {
        socketRef.current.send(blob);
        recordedChunksRef.current = [];
      } else {
        // Queue the chunk when offline or socket not open; accumulate so we send all on reconnect.
        try {
          const meetingId = currentRecordingIdRef.current || currentMeetingDetails?.meeting_id;
          await chunkQueue.addChunk(blob, {
            mimeType: mimeType,
            meetingId: meetingId,
          });
          recordedChunksRef.current = [];
          const queueSize = await chunkQueue.getQueueSize(meetingId);
          uiCallbacks.updateStatus?.(
            `Connection unavailable. ${queueSize} chunk(s) queued for sending when connection is restored.`,
            'warning'
          );
        } catch (error) {
          uiCallbacks.updateStatus?.(
            'Failed to queue chunk. Data may be lost.',
            'error'
          );
        }
      }

      // Optionally update UI
      // uiCallbacks.addTranscript?.("Recording stopped. Data sent.", true);
    };

    recorder.onerror = () => {
      uiCallbacks.addTranscript?.('Recorder error occurred.', true);
      stopRecording();
    };

    recorder.start();
    scheduleNextChunk();

    // Send a keepalive control message so the server (and any proxy) does not close the
    // connection due to idle timeout. We only send audio chunks every 60s or on pause/stop,
    // so without this the connection can be closed at ~10–15s by server/proxy idle timeout.
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      try {
        socketRef.current.send(
          JSON.stringify({
            type: 'control',
            action: 'started',
            duration: getRecordingTime(),
          })
        );
      } catch (e) {
        // Failed to send started keepalive
      }
    }
  };
  const scheduleNextChunk = () => {
    if (chunkTimeoutRef.current) clearTimeout(chunkTimeoutRef.current);
    const chunkDelayMs = firstChunkScheduledRef.current
      ? CHUNK_DURATION_MS - OVERLAP_DURATION_MS
      : INITIAL_CHUNK_DURATION_MS - OVERLAP_DURATION_MS;
    firstChunkScheduledRef.current = true;
    chunkTimeoutRef.current = setTimeout(async () => {
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state === 'recording'
      ) {
        mediaRecorderRef.current.requestData();
        mediaRecorderRef.current.stop();
        await new Promise(r => setTimeout(r, 150));
        await startNewRecorderSegment(streamRef.current);
      }
    }, chunkDelayMs);
  };

  const startRecording = async ({
    useMic = false,
    useSpeaker = false,
    onlyAccess = false,
  } = {}) => {
    if (startRecordingInProgressRef.current) return;
    startRecordingInProgressRef.current = true;
    try {
    // Proactively check microphone permission before starting the meeting.
    // If the permission is blocked, abort early and inform the user.
    if (useMic) {
      try {
        const permResult = await navigator.permissions.query({ name: 'microphone' });
        if (permResult.state === 'denied') {
          dispatch(
            openSnackbar(
              'error',
              'Please allow Microphone access to run Genie Transcribe.',
              true,
              6000
            )
          );
          if (!currentMeetingDetails?.isRecording) {
            dispatch(
              setCurrentRecording({
                isStarted: false,
                isRecording: false,
              })
            );
          }
          return;
        }
      } catch (_) {
        // Permissions API not supported (e.g. Firefox) — fall through to getUserMedia
      }
    }

    // Open PiP synchronously while we still have user activation (same click as Start/Mic/System).
    // This ensures mini PiP is already open when user switches browser tabs later.
    try {
      uiCallbacks.onBeforeStartRecordingRef?.current?.();
    } catch (_) {
      // Ignore PiP errors (e.g. not supported or user dismissed)
    }
    try {
      await ensureWebSocketConnected();
      await waitForServerReady();
    } catch (err) {
      uiCallbacks.updateStatus?.(
        'Unable to start meeting: transcription server is not connected.',
        'error'
      );
      dispatch(
        setCurrentRecording({
          websocketConnection: false,
          isRecording: false,
        })
      );
      return;
    }

    const isCurrentlyRecording =
      currentMeetingDetails?.isRecording && !currentMeetingDetails?.paused;

    // This check determines if the desired state is to have no active sources.
    const willHaveNoSources = !useMic && !useSpeaker;

    if (isCurrentlyRecording && willHaveNoSources) {
      // Pause the recorder BEFORE its audio source tracks are stopped,
      // preserving the data buffer and sending the final chunk.
      await pauseRecording();

      dispatch(
        setCurrentRecording({
          manualPause: true,
        })
      );

      dispatch(
        openSnackbar(
          'info',
          'Meeting paused — no audio source is active. Enable Mic or System Audio to resume.'
        )
      );

      // Now that it's safely paused, clean up the underlying media stream tracks.
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
        micStreamRef.current = null;
        setIsMicActive(false);
      }
      if (speakerStreamRef.current) {
        speakerStreamRef.current.getTracks().forEach(track => track.stop());
        speakerStreamRef.current = null;
        setIsSystemActive(false);
      }

      return;
    }
    // --- Part 0: ALWAYS RESET THE AUDIO CONTEXT ---
    // This is still correct and necessary.
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
    }

    // --- Part 1: Handle Stream REMOVAL (Toggling Off) ---
    // This is also correct.
    if (!useMic && micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;

      setIsMicActive(false);
    }
    if (!useSpeaker && speakerStreamRef.current) {
      speakerStreamRef.current.getTracks().forEach(track => track.stop());
      speakerStreamRef.current = null;
      setIsSystemActive(false);
    }

    // --- Part 2: Handle Stream ACQUISITION (Toggling On) ---
    // This is also correct.
    try {
      const hasLiveMicTrack = micStreamRef.current
        ?.getAudioTracks()
        .some(track => track.readyState === 'live');
      const hasLiveSpeakerTrack = speakerStreamRef.current
        ?.getAudioTracks()
        .some(track => track.readyState === 'live');

      if (useMic && !hasLiveMicTrack) {
        if (micStreamRef.current) {
          micStreamRef.current.getTracks().forEach(track => track.stop());
          micStreamRef.current = null;
        }
        micStreamRef.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
        });
        setIsMicActive(true);
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter(d => d.kind === 'audioinput');

        if (microphones.length === 0) {
          alert(
            'No microphone found. Please connect a microphone and grant permission.'
          );

          // Clean up the stream we just created as it's not usable
          micStreamRef.current.getTracks().forEach(track => track.stop());
          micStreamRef.current = null;

          // Stop the function execution
          return;
        }
        setMicrophones(microphones);
        // Extract only serializable properties from InputDeviceInfo objects
        const serializableMicrophones = microphones.map(device => ({
          deviceId: device.deviceId,
          kind: device.kind,
          label: device.label,
          groupId: device.groupId,
        }));
        dispatch(setCurrentRecording({ microPhoneList: serializableMicrophones }));
        if (!activeMicrophone && microphones.length > 0)
          setActiveMicrophone(microphones[0]);
      }
      if (useSpeaker && !hasLiveSpeakerTrack) {
        if (speakerStreamRef.current) {
          speakerStreamRef.current.getTracks().forEach(track => track.stop());
          speakerStreamRef.current = null;
        }
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        if (stream.getAudioTracks().length > 0) {
          speakerStreamRef.current = stream;
          setIsSystemActive(true);
        } else {
          alert(
            "No audio track found. Please ensure you check the 'Share audio' box."
          );
          stream.getTracks().forEach(track => track.stop());
        }
      }
    } catch (err) {
      // User canceling the system/screen-share modal throws NotAllowedError; don't show mic message or disable mic.
      const isSpeakerShareCancel =
        useSpeaker &&
        (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError');

      if (isSpeakerShareCancel) {
        // Modal closed without sharing; only turn off system audio, leave mic unchanged.
        setIsSystemActive(false);
        // Don't show any snackbar so the modal simply closes without a wrong "microphone" message.
        // Part 0 already closed the AudioContext; rebuild stream from mic only so waveform and recorder work again.
        const hasMicLive = micStreamRef.current
          ?.getAudioTracks()
          .some(t => t.readyState === 'live');
        const state = store.getState().currentMeetingDetails;
        if (
          hasMicLive &&
          state?.isRecording &&
          !state?.paused
        ) {
          try {
            audioCtxRef.current = new (window.AudioContext ||
              window.webkitAudioContext)();
            const destination = audioCtxRef.current.createMediaStreamDestination();
            const splitter = audioCtxRef.current.createGain();
            splitter.gain.value = 1.0;
            const micSource = audioCtxRef.current.createMediaStreamSource(
              micStreamRef.current
            );
            micSource.connect(splitter);
            splitter.connect(destination);
            const finalStream = destination.stream;
            streamRef.current = finalStream;
            await startNewRecorderSegment(finalStream);
          } catch (_) {
            // Recovery failed; waveform may stay off until next user action
          }
        }
      } else if (
        err.name === 'NotAllowedError' ||
        err.name === 'PermissionDeniedError'
      ) {
        dispatch(
          openSnackbar(
            'error',
            'Please allow Microphone access to run Genie Transcribe.',
            true,
            6000
          )
        );
        if (useMic) setIsMicActive(false);
        if (useSpeaker) setIsSystemActive(false);
      } else if (err.name === 'NotFoundError') {
        dispatch(
          openSnackbar(
            'error',
            'Hardware not found. Please ensure your microphone is properly connected and recognized by your operating system.',
            true
          )
        );
        if (useMic) setIsMicActive(false);
        if (useSpeaker) setIsSystemActive(false);
      } else {
        dispatch(
          openSnackbar(
            'error',
            'An error occurred while accessing your media device. It might be in use by another application or there could be a hardware issue.',
            true
          )
        );
        if (useMic) setIsMicActive(false);
        if (useSpeaker) setIsSystemActive(false);
      }

      // Reset meeting start state only when no recording was started (e.g. first-time mic denial).
      // When user cancels system share, meeting continues and mic stays enabled.
      if (!currentMeetingDetails?.isRecording) {
        dispatch(
          setCurrentRecording({
            isStarted: false,
            isRecording: false,
          })
        );
      }

      return;
    }

    if (onlyAccess) {
      return;
    }

    // --- Part 3: Await Previous Recorder Stop ---
    // This is still a good safety measure against race conditions.
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      await new Promise(resolve => {
        mediaRecorderRef.current.onstop = () => {
          mediaRecorderRef.current.onstop = null;
          resolve();
        };
        mediaRecorderRef.current.stop();
      });
    }

    // --- Part 4: THE NEW, CONSISTENT LOGIC ---
    const hasMic = micStreamRef.current
      ?.getAudioTracks()
      .some(t => t.readyState === 'live');
    const hasSpeaker = speakerStreamRef.current
      ?.getAudioTracks()
      .some(t => t.readyState === 'live');

    if (hasMic || hasSpeaker) {
      // --- CASE 1: AT LEAST ONE SOURCE IS ACTIVE ---
      pauseClicked.current = false;
      stopClicked.current = false;

      // We will ALWAYS create an AudioContext and pass its stream to the recorder.
      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
      const destination = audioCtxRef.current.createMediaStreamDestination();

      // Create a "splitter" gain node - this has both inputs AND outputs
      // This allows us to split the audio to both destination (for recording) and VAD analyser
      const splitter = audioCtxRef.current.createGain();
      splitter.gain.value = 1.0; // Pass-through, no volume change

      // Create VAD analyser - commented out (Silero VAD disabled)
      // vadAnalyserRef.current = audioCtxRef.current.createAnalyser();
      // vadAnalyserRef.current.fftSize = 2048; // Higher resolution for VAD

      // Conditionally connect the sources to the splitter (not directly to destination)
      if (hasMic) {
        const micSource = audioCtxRef.current.createMediaStreamSource(
          micStreamRef.current
        );
        micSource.connect(splitter);
      }
      if (hasSpeaker) {
        const speakerSource = audioCtxRef.current.createMediaStreamSource(
          speakerStreamRef.current
        );
        speakerSource.connect(splitter);
      }

      // Splitter outputs to BOTH destination (for recording) AND VAD analyser
      splitter.connect(destination); // For MediaRecorder
      // splitter.connect(vadAnalyserRef.current); // For VAD analysis (Silero VAD disabled)

      // The finalStream is ALWAYS from the AudioContext destination. This is the key.
      const finalStream = destination.stream;
      streamRef.current = finalStream;

      // User re-enabled mic or speaker after "Proceed" (or manual pause). Resume the meeting
      // so RESUME goes away, recording continues, and the blue mic indicator shows again.
      if (currentMeetingDetails?.isRecording && currentMeetingDetails?.paused) {
        await resumeRecording();
        return;
      }

      // Manage the recording state (start/resume).
      if (!currentMeetingDetails?.isRecording) {
        updateAppState({ isRecordingHook: true, isPaused: false });
        startTimer();
        const startTime = new Date()
          .toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
          })
          .toLowerCase();

        dispatch(setCurrentRecording({ start_time: startTime }));
      }
      //  else if (currentMeetingDetails?.paused) {
      //   resumeTimer();
      //   updateAppState({ isPaused: false });
      // }

      await startNewRecorderSegment(finalStream);
    }
    } finally {
      startRecordingInProgressRef.current = false;
    }
  };

  const pauseRecording = async () => {
    pauseClicked.current = true;
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      const pausePromise = new Promise(resolve => {
        mediaRecorderRef.current.addEventListener(
          'stop',
          async () => {
            const blob = new Blob(recordedChunksRef.current, {
              type: 'audio/webm',
            });

            if (canSendChunkNow()) {
              socketRef.current.send(blob);
              pauseChunkPendingRef.current = true;
              recordedChunksRef.current = [];
            } else {
              try {
                const meetingId = currentRecordingIdRef.current || currentMeetingDetails?.meeting_id;
                await chunkQueue.addChunk(blob, {
                  mimeType: 'audio/webm',
                  meetingId: meetingId,
                });
                recordedChunksRef.current = [];
              } catch (error) {
                // Failed to queue chunk during pause
              }
            }

            resolve();
          },
          { once: true }
        );
      });

      mediaRecorderRef.current.stop();
      await pausePromise;

      if (chunkTimeoutRef.current) clearTimeout(chunkTimeoutRef.current);
      if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
      stopTimer();

      updateAppState({ isPaused: true });

      // Also update Redux state so UI reflects the paused state
      // Check if this is a connection-loss pause (not manual pause) by checking:
      // 1. WebSocket is not open, OR
      // 2. Reconnection is in progress (indicating we're pausing due to connection issues)
      const isConnectionLossPause =
        socketRef.current?.readyState !== WebSocket.OPEN ||
        reconnectInProgressRef.current;

      if (isConnectionLossPause) {
        // Paused due to connection loss - set paused in Redux but keep manualPause/autoPaused as false
        // so UI can show "Reconnecting..." state
        dispatch(
          setCurrentRecording({
            paused: true,
            // Don't set manualPause or autoPaused - they should remain false for connection-loss pauses
          })
        );
      } else {
        // Manual pause - set both paused and manualPause in Redux
        // Only set manualPause if it wasn't already set (to avoid overwriting autoPaused)
        const currentState = store.getState().currentMeetingDetails;
        if (!currentState?.autoPaused) {
          dispatch(
            setCurrentRecording({
              paused: true,
              manualPause: true,
            })
          );
        }
      }

      await new Promise(r => setTimeout(r, 200));

      if (uiCallbacks.updateStatus) {
        uiCallbacks.updateStatus(
          'Recording stopped. Final transcript sent.',
          'info'
        );
      }
    }
    // Releasing the recording lock here ensures that when the meeting is paused
    // (either manually or due to connection loss), the tab is no longer kept
    // artificially active.
    releaseRecordingLock();
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: 'control',
          action: 'pause',
          duration: getRecordingTime(),
        })
      );
    }
  };

  const autoPauseRecording = async () => {
    pauseClicked.current = true;
    // Similar to pauseRecording but sets autoPaused flag
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      const pausePromise = new Promise(resolve => {
        mediaRecorderRef.current.addEventListener(
          'stop',
          async () => {
            const blob = new Blob(recordedChunksRef.current, {
              type: 'audio/webm',
            });

            if (canSendChunkNow()) {
              socketRef.current.send(blob);
              pauseChunkPendingRef.current = true;
              recordedChunksRef.current = [];
            } else {
              try {
                const meetingId = currentRecordingIdRef.current || currentMeetingDetails?.meeting_id;
                await chunkQueue.addChunk(blob, {
                  mimeType: 'audio/webm',
                  meetingId: meetingId,
                });
                recordedChunksRef.current = [];
              } catch (error) {
                // Failed to queue chunk during auto-pause
              }
            }

            resolve();
          },
          { once: true }
        );
      });

      mediaRecorderRef.current.stop();
      await pausePromise;

      if (chunkTimeoutRef.current) clearTimeout(chunkTimeoutRef.current);
      if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
      stopTimer();

      updateAppState({ isPaused: true });
      dispatch(
        setCurrentRecording({
          paused: true,
          autoPaused: true,
        })
      );

      await new Promise(r => setTimeout(r, 200));

      if (uiCallbacks.updateStatus) {
        uiCallbacks.updateStatus(
          'Recording paused due to inactivity.',
          'warning'
        );
      }
    }
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: 'control',
          action: 'pause',
          duration: getRecordingTime(),
        })
      );
    }
    // Auto-pause should also release the lock so that if the user leaves the
    // tab idle, the browser is free to fully background it.
    releaseRecordingLock();
  };

  const resumeRecording = async (skipWebSocketReconnect = false) => {
    if (!stateRef.current.isRecordingHook || !stateRef.current.isPaused) return;

    // Check if stream exists before trying to resume
    if (!streamRef.current) {
      dispatch(
        openSnackbar(
          'error',
          'Cannot resume: Audio stream is not available. Please start recording first.'
        )
      );
      return;
    }

    try {
      // Intentional WS close/reconnect below nulls onclose — no need to suppress
      // reconnect globally; doing so left suppress true after auto-resume and
      // blocked all subsequent socket.onclose reconnect scheduling.

      // Wait for the server to respond before closing old WS.
      // Covers two cases:
      //   1) recording_id not yet received (first chunk) — avoids duplicate meeting
      //   2) pause-chunk transcript not yet received — avoids losing the transcript
      const needRecordingId = !currentRecordingIdRef.current;
      const needPauseTranscript = pauseChunkPendingRef.current;
      if (!skipWebSocketReconnect && (needRecordingId || needPauseTranscript)) {
        const RESUME_WAIT_TIMEOUT_MS = 10000;
        dispatch(setCurrentRecording({ resumeWaitingForServer: true }));
        uiCallbacks.updateStatus?.('Waiting for server response...', 'info');
        try {
          const promises = [];
          if (needRecordingId) {
            promises.push(new Promise(resolve => {
              waitForRecordingIdResolveRef.current = resolve;
            }));
          }
          if (needPauseTranscript) {
            promises.push(new Promise(resolve => {
              waitForPauseTranscriptResolveRef.current = resolve;
            }));
          }
          await Promise.race([
            Promise.all(promises),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('timeout')), RESUME_WAIT_TIMEOUT_MS)
            ),
          ]);
        } catch {
          // Timeout -- proceed anyway; server may assign the ID on reconnect
        } finally {
          waitForRecordingIdResolveRef.current = null;
          waitForPauseTranscriptResolveRef.current = null;
          pauseChunkPendingRef.current = false;
          dispatch(setCurrentRecording({ resumeWaitingForServer: false }));
        }
      }

      // Only close and reconnect websocket if skipWebSocketReconnect is false
      // This allows auto-resume after reconnection to skip the close/reconnect step
      if (!skipWebSocketReconnect) {
        if (socketRef.current) {
          socketRef.current.onclose = null; // <-- detach reconnect logic
          socketRef.current.onerror = null;
          socketRef.current.close();
          socketRef.current = null;
        }
        if (pingTimeoutRef.current) {
          clearTimeout(pingTimeoutRef.current);
          pingTimeoutRef.current = null;
        }
        if (reconnectTimeoutsRef.current) {
          reconnectTimeoutsRef.current.forEach(id => clearTimeout(id));
          reconnectTimeoutsRef.current.clear();
        }
      }

      //  Reset reconnect state
      retryRef.current = 0;
      reconnectInProgressRef.current = false;
      pauseClicked.current = false;

      dispatch(
        setCurrentRecording({
          paused: false,
          manualPause: false,
          autoPaused: false,
        })
      ); // Reset both pause flags
      updateAppState({ isPaused: false });
      isResumingRef.current = true;
      // Initialize timer from Redux state (includes duration from history resume)
      recordingTimeRef.current = getRecordingTime();
      resumeTimer();
      await startNewRecorderSegment(streamRef.current);

      // Only connect websocket if we're not skipping the reconnect
      if (!skipWebSocketReconnect) {
        connectWebSocket();
      }
    } catch (err) {
      dispatch(openSnackbar('error', 'Failed to resume the meeting.'));
      updateAppState({ isPaused: true }); // Revert state on failure
    } finally {
      // Never leave suppression stuck after resume (fatal server errors set
      // suppress via a separate path and do not use resumeRecording).
      suppressReconnectRef.current = false;
    }
  };
  const stopRecording = async (socketAction = 'stop') => {
    // Stop the timer first and capture final duration from ref (source of truth).
    // This ensures we send the correct duration to the server regardless of Redux
    // sync timing, and that the ref is not updated anymore during stop.
    stopTimer();
    const finalDuration = Math.max(
      0,
      Number.isFinite(recordingTimeRef.current) ? Math.floor(recordingTimeRef.current) : 0
    );

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      const stopPromise = new Promise(resolve => {
        mediaRecorderRef.current.addEventListener(
          'stop',
          async () => {
            const blob = new Blob(recordedChunksRef.current, {
              type: 'audio/webm',
            });

            if (canSendChunkNow()) {
              socketRef.current.send(blob);
              recordedChunksRef.current = [];
            } else {
              try {
                const meetingId = currentRecordingIdRef.current || currentMeetingDetails?.meeting_id;
                await chunkQueue.addChunk(blob, {
                  mimeType: 'audio/webm',
                  meetingId: meetingId,
                });
                recordedChunksRef.current = [];
              } catch (error) {
                // Failed to queue chunk during stop
              }
            }

            resolve();
          },
          { once: true }
        );
      });

      mediaRecorderRef.current.stop();
      await stopPromise;

      if (chunkTimeoutRef.current) clearTimeout(chunkTimeoutRef.current);
      if (responseTimeoutRef.current) clearTimeout(responseTimeoutRef.current);
      isResumingRef.current = false;

      updateAppState({
        isRecordingHook: false,
        isPaused: false,
      });

      await new Promise(r => setTimeout(r, 200));

      if (uiCallbacks.updateStatus) {
        uiCallbacks.updateStatus(
          'Recording stopped. Final transcript sent.',
          'info'
        );
      }
    }
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: 'control',
          action: socketAction,
          duration: finalDuration,
        })
      );
    }
    dispatch(
      setCurrentRecording({
        websocketConnection: false,
        paused: false,
        manualPause: false,
        autoPaused: false,
        fromHistoryResume: false,
        parentMeetId: '',
        recordingTime: finalDuration,
      })
    );
    stopClicked.current = true;
    firstChunkScheduledRef.current = false;
    // Clear chunk queue when meeting stops
    const meetingId = currentRecordingIdRef.current || currentMeetingDetails?.meeting_id;
    chunkQueue.clearQueue(meetingId).catch(() => { });

    // Wait for both (1) recording_id and (2) the server's response message (final
    // transcript for the last chunk) before closing – unless server already reported
    // no recording (LOW_SIGNAL_AUDIO / NO_VOICE_ACTIVITY / etc.): then skip wait and close immediately.
    const noRecordingExpected = store.getState()?.currentMeetingDetails?.noRecordingExpected;
    const shouldWaitForResponse =
      socketRef.current?.readyState === WebSocket.OPEN &&
      socketAction !== 'stop_unload' &&
      !noRecordingExpected;

    if (shouldWaitForResponse) {
      const SAFETY_TIMEOUT_MS = 90000; // 90s fallback only if server never responds

      const waitForNextMessage = new Promise(resolve => {
        waitForNextMessageResolveRef.current = resolve;
      });

      const waitForRecordingId = currentRecordingIdRef.current
        ? Promise.resolve()
        : new Promise(resolve => {
          waitForRecordingIdResolveRef.current = resolve;
        });

      const safetyTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), SAFETY_TIMEOUT_MS)
      );

      try {
        await Promise.race(
          [
            Promise.all([waitForNextMessage, waitForRecordingId]),
            safetyTimeout,
          ]
        );
      } catch {
        // Safety timeout – proceed to close socket
      } finally {
        waitForRecordingIdResolveRef.current = null;
        waitForNextMessageResolveRef.current = null;
      }

      // Brief grace for any trailing message
      await new Promise(r => setTimeout(r, 500));
    }

    if (pingTimeoutRef.current) {
      clearTimeout(pingTimeoutRef.current);
      pingTimeoutRef.current = null;
    }
    if (socketRef.current) {
      // Detach reconnect/error handlers to prevent unwanted reconnect logic
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;

      if (socketAction === 'stop_unload') {
        // Page is being unloaded – close immediately since the tab is going away
        socketRef.current.close();
        socketRef.current = null;
      } else {
        // Normal stop: connection was kept open until recording_id was received (or timeout).
        // Close the socket now so the UI can safely use meeting_id for summarize/share.
        const pendingSocket = socketRef.current;
        socketRef.current = null; // Prevent other code from using it for sending

        try {
          if (
            pendingSocket.readyState !== WebSocket.CLOSED &&
            pendingSocket.readyState !== WebSocket.CLOSING
          ) {
            pendingSocket.close();
          }
        } catch (e) {
          // Ignore close errors during cleanup
        }
      }
    }
    // Clean up all audio/video tracks
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }

    if (speakerStreamRef.current) {
      speakerStreamRef.current.getTracks().forEach(track => track.stop());
      speakerStreamRef.current = null;
    }

    // Also cleanup analyser and audioContext (if used for waveform)
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (streamRefVoice.current) {
      streamRefVoice.current.getTracks().forEach(track => track.stop());
      streamRefVoice.current = null;
    }

    cancelAnimationFrame(animationRef.current);
    // Meeting has fully stopped – release any held recording lock.
    releaseRecordingLock();
  };

  const clearMicSpeaker = useCallback(() => {
    setIsMicActive(false);
    setIsSystemActive(false);
  }, []);

  const clearMicList = useCallback(() => {
    setVolume(0);
    setMicrophones([]);
    setActiveMicrophone(null);
  }, []);

  const generateMOM = async context => {
    const recordingId = currentRecordingIdRef.current;
    if (!recordingId) return;
    uiCallbacks.updateStatus?.('Generating MOM...', 'info');
    const payload = {
      recording_id: recordingId,
      meeting_description: context || 'General Meeting',
    };
    const res = await fetch('/api/generate-mom', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (data.minutes) {
      uiCallbacks.displayMomContent?.(data.minutes.trim());
    } else {
      uiCallbacks.displayMomContent?.('', true, data.error || 'Unknown error');
    }
  };

  return {
    ...state,
    streamRef,
    vadAnalyserRef, // Export for VAD hook to use
    audioCtxRef, // Export for VAD hook to use
    microphones,
    activeMicrophone,
    volume,
    microphoneError,
    isMicActive,
    isSystemActive,
    selectMicrophone,
    connectWebSocket,
    startRecording,
    stopRecording,
    pauseRecording,
    autoPauseRecording, // Export for VAD hook to use
    resumeRecording,
    generateMOM,
    resetTimer,
    clearMicSpeaker,
    clearMicList,
    resetAll,
  };
}